import { createContext, useReducer, useCallback } from "react";

import axios from "../../utils/axios/v1/userAxios";

const GET_ALL_FIREWALL = "GET_ALL_FIREWALL";
const GET_SINGLE_FIREWALL = "GET_SINGLE_FIREWALL";

const GET_TOP_SOURCE = "GET_TOP_SOURCE";
const GET_TOP_PATH = "GET_TOP_PATH";
const GET_TOP_UA = "GET_TOP_UA";
const GET_TOP_METHOD = "GET_TOP_METHOD";
const GET_TOP_JA3_HASH = "GET_TOP_JA3_HASH";
const GET_TOP_RESPONSE_CODE = "GET_TOP_RESPONSE_CODE";

const SET_ERROR = "SET_ERROR";

const initialState = {
  firewalls: null,
  curfirewall: null,

  top_source: null,
  top_path: null,
  top_ua: null,
  top_method: null,
  top_ja3_hash: null,
  top_response_code: null,

  errMsg: null,
};

const LLMFirewallReducer = (state, action) => {
  switch (action.type) {
    case GET_ALL_FIREWALL:
      return {
        ...state,
        firewalls: action.payload.firewalls,
      };
    case GET_SINGLE_FIREWALL:
      return {
        ...state,
        curfirewall: action.payload.curfirewall,
      };
    case GET_TOP_SOURCE:
      return {
        ...state,
        top_source: action.payload.top_source,
      };
    case GET_TOP_PATH:
      return {
        ...state,
        top_path: action.payload.top_path,
      };
    case GET_TOP_UA:
      return {
        ...state,
        top_ua: action.payload.top_ua,
      };
    case GET_TOP_METHOD:
      return {
        ...state,
        top_method: action.payload.top_method,
      };
    case GET_TOP_JA3_HASH:
      return {
        ...state,
        top_ja3_hash: action.payload.top_ja3_hash,
      };
    case GET_TOP_RESPONSE_CODE:
      return {
        ...state,
        top_response_code: action.payload.top_response_code,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const LLMFirewallContext = createContext(null);

function LLMFirewallProvider({ children }) {
  const [state, dispatch] = useReducer(LLMFirewallReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);
  const getFirewalls = useCallback(
    async (zoneUid, init = false) => {
      if (init)
        dispatch({
          type: GET_ALL_FIREWALL,
          payload: {
            firewalls: null,
          },
        });
      try {
        const response = await axios.get(`/config/llmfw/${zoneUid}`);
        dispatch({
          type: GET_ALL_FIREWALL,
          payload: {
            firewalls: response.data,
          },
        });
      } catch (err) {
        dispatch({
          type: GET_ALL_FIREWALL,
          payload: {
            firewalls: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const updateFirewall = useCallback(
    async (zoneUid, firewallID, value) => {
      try {
        await axios.patch(`/config/llmfw/${zoneUid}/${firewallID}`, value);
        return { msg: "Success", status: "success" };
      } catch (err) {
        setErr(err.message);
        getFirewalls(zoneUid);
        let error_msg = err.message;
        return { msg: error_msg, status: "error" };
      }
    },
    [setErr, getFirewalls]
  );
  const deleteFirewall = useCallback(
    async (zoneUid, value) => {
      try {
        await axios.delete(`config/llmfw/${zoneUid}`, { data: value });
        getFirewalls(zoneUid, true);
      } catch (err) {
        setErr(err.message);
      }
    },
    [getFirewalls, setErr]
  );
  const addNewFirewall = useCallback(
    async (zoneUid, value) => {
      try {
        await axios.post(`/config/llmfw/${zoneUid}`, value);
        return { msg: "Success", status: "success" };
      } catch (err) {
        setErr(err.message);
        let error_msg = err.message;
        return { msg: error_msg, status: "error" };
      }
    },
    [setErr]
  );
  const getCurrentFirewall = useCallback(
    async (zoneUid, firewallID) => {
      try {
        const response = await axios.get(`/config/llmfw/${zoneUid}/${firewallID}`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return [];
      }
    },
    [setErr]
  );
  const saveFwRulesOrder = useCallback(
    async (zoneUid, value) => {
      try {
        await axios.patch(`/config/llmfw/${zoneUid}/set_order`, value);
        return { msg: "Success", status: "success" };
      } catch (err) {
        setErr(err.message);
        let error_msg = err.message;
        return { msg: error_msg, status: "error" };
      }
    },
    [setErr]
  );
  const getTopSource = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_SOURCE,
        payload: {
          top_source: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_fw_stats/top_source",
          {
            zone_id: zone_id,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_SOURCE,
          payload: {
            top_source: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_SOURCE,
          payload: {
            top_source: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopPath = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_PATH,
        payload: {
          top_path: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_fw_stats/top_path",
          {
            zone_id: zone_id,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_PATH,
          payload: {
            top_path: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_PATH,
          payload: {
            top_path: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopUA = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_UA,
        payload: {
          top_ua: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_fw_stats/top_ua",
          {
            zone_id: zone_id,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_UA,
          payload: {
            top_ua: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_UA,
          payload: {
            top_ua: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopMethod = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_METHOD,
        payload: {
          top_method: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_fw_stats/top_method",
          {
            zone_id: zone_id,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_METHOD,
          payload: {
            top_method: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_METHOD,
          payload: {
            top_method: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopJa3Hash = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_JA3_HASH,
        payload: {
          top_ja3_hash: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_fw_stats/top_ja3_hash",
          {
            zone_id: zone_id,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_JA3_HASH,
          payload: {
            top_ja3_hash: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_JA3_HASH,
          payload: {
            top_ja3_hash: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopResponseCode = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_RESPONSE_CODE,
        payload: {
          top_response_code: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_fw_stats/top_res_code",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_RESPONSE_CODE,
          payload: {
            top_response_code: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_RESPONSE_CODE,
          payload: {
            top_response_code: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopFwEventsInfo = useCallback(
    (setLLMEventController, zone_id, time_range) => {
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);

      getTopSource(zone_id, time_range, undefined, signal);
      getTopPath(zone_id, time_range, undefined, signal);
      getTopUA(zone_id, time_range, undefined, signal);
      getTopMethod(zone_id, time_range, undefined, signal);
      getTopResponseCode(zone_id, time_range, undefined, signal);
      getTopJa3Hash(zone_id, time_range, undefined, signal);
    },
    [getTopSource, getTopPath, getTopUA, getTopMethod, getTopResponseCode, getTopJa3Hash]
  );
  return (
    <LLMFirewallContext.Provider
      value={{
        ...state,
        setErr,
        getFirewalls,
        updateFirewall,
        deleteFirewall,
        addNewFirewall,
        getCurrentFirewall,
        saveFwRulesOrder,
        getTopSource,
        getTopPath,
        getTopUA,
        getTopMethod,
        getTopJa3Hash,
        getTopFwEventsInfo,
      }}
    >
      {children}
    </LLMFirewallContext.Provider>
  );
}

export { LLMFirewallContext, LLMFirewallProvider };
